import { useEffect, useMemo, useState } from "react";
import { useGameStore, SCREEN } from "@/stores/gameStore";
import { useMatchStore } from "@/stores/matchStore";
import { MATCH } from "@/constants/testIds";
import { Audio_ } from "@/lib/audio";
import { recordMatch, fetchProfile } from "@/lib/api";
import { toast } from "sonner";
import { Copy, Check, Share2 } from "lucide-react";
import { BRAND } from "@/config/game";

const MAP_LABELS = {
    training_yard: "Training Yard",
    factory: "Factory",
    warehouse: "Warehouse",
    desert_base: "Desert Base",
};

const DIFFICULTY_LABELS = {
    easy: "Easy",
    normal: "Normal",
    hard: "Hard",
    expert: "Expert",
    impossible: "Impossible",
};

/**
 * Build the shareable recap string. Kept pure so it's easy to test.
 */
export function buildRecap({ won, config, kills, deaths, scoreYou, scoreEnemy, durationSec }) {
    const map = MAP_LABELS[config.mapId] || config.mapId;
    const difficulty = DIFFICULTY_LABELS[config.difficulty] || config.difficulty;
    const outcome = won ? "🏆 VICTORY" : "💀 DEFEAT";
    const mm = Math.floor(durationSec / 60);
    const ss = String(durationSec % 60).padStart(2, "0");
    return [
        `${BRAND.game.title} · ${outcome}`,
        `Map · ${map}`,
        `Team · ${config.teamSize}  |  Bots · ${difficulty}`,
        `Score · ${scoreYou}-${scoreEnemy}`,
        `K/D · ${kills}/${deaths}`,
        `Time · ${mm}:${ss}`,
        `— by ${BRAND.company.name}`,
    ].join("\n");
}

export default function MatchEndScreen({ config }) {
    const goto = useGameStore((s) => s.goto);
    const setLastMatch = useGameStore((s) => s.setLastMatch);
    const m = useMatchStore();
    const [copied, setCopied] = useState(false);

    const won = m.winner === "you";
    const durationSec = Math.max(0, Math.floor((performance.now() - m.matchStartAt) / 1000));

    const recap = useMemo(
        () =>
            buildRecap({
                won,
                config,
                kills: m.kills,
                deaths: m.deaths,
                scoreYou: m.scoreYou,
                scoreEnemy: m.scoreEnemy,
                durationSec,
            }),
        // durationSec is stable at mount time; deps intentionally narrow
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    useEffect(() => {
        Audio_.play(won ? "victory" : "defeat", { volume: 0.7 });
        (async () => {
            try {
                const profile = await fetchProfile();
                await recordMatch({
                    profile_id: profile.id,
                    map: config.mapId,
                    mode: "training",
                    team_size: config.teamSize,
                    difficulty: config.difficulty,
                    kills: m.kills,
                    deaths: m.deaths,
                    rounds_won: m.scoreYou,
                    rounds_lost: m.scoreEnemy,
                    won,
                    duration_seconds: durationSec,
                });
                setLastMatch({ won, kills: m.kills, deaths: m.deaths });
                toast.success(`Match saved · +${m.kills * 25 + (won ? 150 : 60)} XP`);
            } catch (e) {
                toast.error("Could not save match stats.");
            }
        })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const copyRecap = async () => {
        try {
            if (navigator.clipboard?.writeText) {
                await navigator.clipboard.writeText(recap);
            } else {
                // Fallback for insecure contexts
                const ta = document.createElement("textarea");
                ta.value = recap;
                document.body.appendChild(ta);
                ta.select();
                document.execCommand("copy");
                document.body.removeChild(ta);
            }
            setCopied(true);
            toast.success("Recap copied — paste it anywhere");
            setTimeout(() => setCopied(false), 2200);
        } catch (e) {
            toast.error("Copy failed — long-press the recap card to copy manually");
        }
    };

    const share = async () => {
        // Web Share API — falls back to copy when unsupported
        if (navigator.share) {
            try {
                await navigator.share({ title: `${BRAND.game.title} match recap`, text: recap });
                return;
            } catch (e) {
                // ignored — fall through to copy
            }
        }
        copyRecap();
    };

    return (
        <div
            data-testid={MATCH.endScreen}
            className="fixed inset-0 z-40 flex items-center justify-center bg-black/85 backdrop-blur-xl axis-fade-in"
        >
            <div className="max-w-2xl w-[92%] axis-glass axis-cut p-10 border-cyan-400/30">
                <div className="text-center mb-8">
                    <div className="axis-mono text-[10px] tracking-[0.6em] text-white/50 mb-2">
                        MATCH COMPLETE
                    </div>
                    <div
                        data-testid={MATCH.endTitle}
                        className="axis-heading text-6xl tracking-[0.3em] axis-metallic axis-text-glow"
                        style={{ color: won ? "#00e5ff" : "#ff2a2a" }}
                    >
                        {won ? "VICTORY" : "DEFEAT"}
                    </div>
                </div>

                <div className="grid grid-cols-4 gap-3 mb-6">
                    <EndStat label="Kills" value={m.kills} color="#00e5ff" />
                    <EndStat label="Deaths" value={m.deaths} color="#ff2a2a" />
                    <EndStat label="Rounds W" value={m.scoreYou} />
                    <EndStat label="Rounds L" value={m.scoreEnemy} />
                </div>

                {/* Recap card preview */}
                <div
                    data-testid="match-recap-card"
                    className="axis-cut-sm border border-white/10 bg-black/40 p-4 mb-6"
                >
                    <div className="flex items-center justify-between mb-2">
                        <span className="axis-mono text-[10px] tracking-[0.4em] text-cyan-300/80">
                            RECAP CARD
                        </span>
                        <span className="axis-mono text-[9px] tracking-[0.3em] text-white/40">
                            READY TO SHARE
                        </span>
                    </div>
                    <pre
                        data-testid="match-recap-text"
                        className="axis-mono text-[11px] leading-[1.55] text-white/85 whitespace-pre-wrap"
                        style={{ fontFamily: "'JetBrains Mono', monospace" }}
                    >
                        {recap}
                    </pre>
                </div>

                <div className="flex flex-wrap justify-center gap-3">
                    <button
                        data-testid="match-copy-recap"
                        onClick={copyRecap}
                        className="axis-btn axis-btn-ghost axis-cut-sm px-6 py-3 flex items-center gap-2 min-w-[180px] justify-center"
                    >
                        {copied ? <Check size={14} className="text-cyan-300" /> : <Copy size={14} />}
                        {copied ? "Copied" : "Copy Recap"}
                    </button>
                    <button
                        data-testid="match-share-recap"
                        onClick={share}
                        className="axis-btn axis-btn-ghost axis-cut-sm px-6 py-3 flex items-center gap-2 min-w-[140px] justify-center"
                    >
                        <Share2 size={14} /> Share
                    </button>
                    <button
                        data-testid={MATCH.returnMenu}
                        onClick={() => goto(SCREEN.MENU)}
                        className="axis-btn axis-btn-primary axis-cut-sm px-8 py-3 min-w-[200px]"
                    >
                        Return to Menu
                    </button>
                </div>
            </div>
        </div>
    );
}

function EndStat({ label, value, color }) {
    return (
        <div className="axis-cut-sm border border-white/10 p-3 text-center bg-white/[0.02]">
            <div className="axis-mono text-[10px] tracking-[0.4em] text-white/50">{label}</div>
            <div className="axis-hud text-4xl mt-1" style={{ color: color || "#fff" }}>
                {value}
            </div>
        </div>
    );
}
